import React, { useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Loader2, Play, Download, Plus, Target, ChevronUp, ChevronDown, ChevronsUpDown,
  BookmarkCheck, ArrowUpRight,
} from "lucide-react";
import { formatCpc, currencySymbolForLocation } from "../../lib/currency";
import { rowsToCsv, downloadCsv, slugForFilename, todayStamp } from "../../lib/csv";

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

const SORT_ACCESSORS = {
  search_volume: (r) => r.search_volume,
  cpc: (r) => r.cpc,
  keyword_difficulty: (r) => r.keyword_difficulty,
  competitor_rank: (r) => r.competitor_rank,
};

function SortHeader({ label, sortKey, currentKey, currentDir, onClick }) {
  const active = currentKey === sortKey;
  const Icon = !active ? ChevronsUpDown : currentDir === "asc" ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      data-testid={`gap-sort-${sortKey}`}
      className={`inline-flex items-center gap-1 hover:text-[#1a202c] transition-colors group/sort ${active ? "text-[#1a202c]" : ""}`}
    >
      <span>{label}</span>
      <Icon className={`h-3 w-3 ${active ? "opacity-100 text-[#2c7a7b]" : "opacity-40"}`} strokeWidth={2.5} />
    </button>
  );
}

export default function ContentGapCard({ client, reloadClient }) {
  const competitors = client.competitors || [];
  const [competitorDomain, setCompetitorDomain] = useState(competitors[0] || "");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [filterQuickWins, setFilterQuickWins] = useState(false);
  // Default: biggest opportunities first — search volume descending.
  const [sortKey, setSortKey] = useState("search_volume");
  const [sortDir, setSortDir] = useState("desc");

  // Keep dropdown valid if the competitor list changes under us.
  React.useEffect(() => {
    if (competitors.length && !competitors.includes(competitorDomain)) {
      setCompetitorDomain(competitors[0]);
    }
  }, [competitors, competitorDomain]);

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      // Volume + CPC default to desc; rank/KD default to asc (lower = better).
      setSortDir(key === "competitor_rank" || key === "keyword_difficulty" ? "asc" : "desc");
    } else if (sortDir === "desc") setSortDir("asc");
    else { setSortKey(null); setSortDir(null); }
  };

  const items = result?.items || [];

  const filtered = useMemo(() => {
    if (!filterQuickWins) return items;
    // "Quick wins" = decent volume, low difficulty, competitor already in top 20
    // so the term is clearly reachable in this niche.
    return items.filter((r) =>
      (r.search_volume ?? 0) >= 100 &&
      (r.keyword_difficulty ?? 100) <= 30 &&
      (r.competitor_rank ?? 999) <= 20
    );
  }, [items, filterQuickWins]);

  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return filtered;
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return filtered;
    const decorated = filtered.map((r, i) => ({ r, i, v: accessor(r) }));
    decorated.sort((a, b) => {
      const aNull = a.v == null, bNull = b.v == null;
      if (aNull && bNull) return a.i - b.i;
      if (aNull) return 1;
      if (bNull) return -1;
      if (a.v === b.v) return a.i - b.i;
      return sortDir === "asc" ? a.v - b.v : b.v - a.v;
    });
    return decorated.map((d) => d.r);
  }, [filtered, sortKey, sortDir]);

  const selectableKeywords = sorted
    .filter((r) => !r.already_tracked)
    .map((r) => r.keyword);
  const allChecked = selected.size > 0 && selected.size === selectableKeywords.length;

  const toggleOne = (kw) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw);
      else next.add(kw);
      return next;
    });
  };
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(selectableKeywords));
  };

  const run = async () => {
    if (!competitorDomain) {
      toast.error("Pick a competitor to compare against.");
      return;
    }
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await api.post(`/clients/${client.id}/competitors/content-gap`, {
        competitor_domain: competitorDomain,
        limit,
      });
      setResult(data);
      if (!data.items?.length) {
        toast.info(`No gap keywords found — ${competitorDomain} and ${client.domain} share none of their top terms.`);
      } else {
        toast.success(`Found ${data.items.length} gap keyword${data.items.length > 1 ? "s" : ""} vs ${competitorDomain}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Content gap lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const addToTracked = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      // Target the campaign-type-appropriate bucket so the new keywords actually
      // get polled on the matching SERP location.
      const isLocal = client.campaign_type === "local";
      const field = isLocal ? "local_keywords" : "national_keywords";
      const existing = client[field] || [];
      const seen = new Set(existing.map((k) => k.toLowerCase()));
      const merged = [...existing];
      for (const k of selected) {
        if (!seen.has(k.toLowerCase())) {
          seen.add(k.toLowerCase());
          merged.push(k);
        }
      }
      await api.patch(`/clients/${client.id}`, { [field]: merged });
      toast.success(`Added ${selected.size} keyword${selected.size > 1 ? "s" : ""} to ${isLocal ? "local" : "national"} tracked list.`);
      // Mirror into the local result so the "already tracked" badge updates
      // immediately — saves a round-trip for the user visually.
      setResult((prev) => prev ? {
        ...prev,
        items: prev.items.map((r) => selected.has(r.keyword) ? { ...r, already_tracked: true } : r),
      } : prev);
      setSelected(new Set());
      reloadClient?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add keywords");
    } finally {
      setAdding(false);
    }
  };

  const exportCsv = () => {
    if (!sorted.length) return;
    const currency = currencySymbolForLocation(client.location_name).trim() || "$";
    const columns = [
      { header: "Keyword", accessor: (r) => r.keyword },
      { header: "Tracked", accessor: (r) => r.already_tracked ? "Yes" : "No" },
      { header: "Competitor Rank", accessor: (r) => r.competitor_rank ?? "" },
      { header: "Search Volume", accessor: (r) => r.search_volume ?? "" },
      { header: `CPC (${currency})`, accessor: (r) => r.cpc ?? "" },
      { header: "KD", accessor: (r) => r.keyword_difficulty ?? "" },
      { header: "Competition", accessor: (r) => r.competition_level ?? "" },
      { header: "Competitor URL", accessor: (r) => r.competitor_url ?? "" },
      { header: "Client Domain", accessor: () => client.domain },
      { header: "Competitor Domain", accessor: () => competitorDomain },
      { header: "Location", accessor: () => client.location_name || "" },
    ];
    const csv = rowsToCsv(sorted, columns);
    const filename = `${slugForFilename(client.domain)}-vs-${slugForFilename(competitorDomain)}-gap-${todayStamp()}.csv`;
    downloadCsv(filename, csv);
    toast.success(`Exported ${sorted.length} gap keywords`);
  };

  return (
    <div className="card-soft p-5 space-y-4" data-testid="content-gap-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold flex items-center gap-1.5">
            <Target className="h-3 w-3" /> Content gap
          </div>
          <h3 className="font-display text-xl mt-0.5 text-[#1a202c]">Keywords competitors rank for — and you don't</h3>
          <p className="text-xs text-[#718096] mt-1">
            Pick a competitor and we'll surface the keywords they rank for in Google <span className="font-mono-tight">{client.location_name || "UK"}</span>{" "}
            where <span className="font-mono-tight">{client.domain}</span> is nowhere to be seen in the top 100.
          </p>
        </div>
      </div>

      {/* Run bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={competitorDomain}
          onChange={(e) => setCompetitorDomain(e.target.value)}
          disabled={loading || competitors.length === 0}
          data-testid="gap-competitor-select"
          className="bg-white border border-[#e2e8f0] rounded-xl px-3.5 py-2 text-sm font-mono-tight focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all min-w-[220px]"
        >
          {competitors.length === 0 && <option value="">No competitors added</option>}
          {competitors.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value, 10))}
          disabled={loading}
          data-testid="gap-limit-select"
          className="bg-white border border-[#e2e8f0] rounded-xl px-3.5 py-2 text-sm font-mono-tight focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
          title="Max keywords to pull from DataForSEO"
        >
          <option value={50}>Top 50</option>
          <option value={100}>Top 100</option>
          <option value={250}>Top 250</option>
          <option value={500}>Top 500</option>
        </select>
        <button
          onClick={run}
          disabled={loading || !competitorDomain || client.frozen}
          title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
          data-testid="gap-run"
          className="btn-primary inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          {loading ? "Finding gap…" : "Find gap keywords"}
        </button>
        {result && (
          <div className="text-[11px] text-[#a0aec0] font-mono-tight ml-auto">
            {sorted.length} / {items.length} rows{result.total_count ? ` · ${result.total_count} total available` : ""}
          </div>
        )}
      </div>

      {/* Empty states */}
      {competitors.length === 0 && (
        <div className="rounded-xl bg-[#fffaf0] border border-[#feebc8] text-[#744210] text-xs px-4 py-3">
          Add at least one competitor domain above before running a content-gap lookup.
        </div>
      )}

      {result && items.length === 0 && (
        <div className="rounded-xl bg-[#f7fafc] border border-[#edf2f7] text-[#4a5568] text-xs px-4 py-3">
          No gap keywords found — the competitor doesn't rank for anything you don't already cover.
        </div>
      )}

      {/* Toolbar: filter + bulk actions + export */}
      {items.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button
            onClick={() => setFilterQuickWins((v) => !v)}
            data-testid="gap-quick-wins"
            className={`text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors ${
              filterQuickWins ? "bg-[#4fd1c5] text-white hover:bg-[#38b2ac]" : "bg-[#4fd1c5]/10 text-[#2c7a7b] hover:bg-[#4fd1c5]/20"
            }`}
            title="Volume ≥ 100, KD ≤ 30, competitor ranking in top 20"
          >
            Quick wins
          </button>
          {selected.size > 0 && (
            <button
              onClick={addToTracked}
              disabled={adding}
              data-testid="gap-add-tracked"
              className="btn-primary inline-flex items-center gap-2"
            >
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add {selected.size} to tracked
            </button>
          )}
          <button
            onClick={exportCsv}
            data-testid="gap-export-csv"
            className="btn-ghost inline-flex items-center gap-2 ml-auto"
            title="Download the current view as CSV"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      )}

      {/* Results table */}
      {items.length > 0 && (
        <div className="rounded-xl border border-[#edf2f7] overflow-hidden">
          <div className="grid grid-cols-12 gap-x-3 px-4 py-3 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
            <div className="col-span-1 flex items-center">
              <button
                onClick={toggleAll}
                disabled={selectableKeywords.length === 0}
                data-testid="gap-toggle-all"
                className="h-4 w-4 rounded border border-[#cbd5e0] bg-white inline-flex items-center justify-center hover:border-[#4fd1c5] transition-colors disabled:opacity-50"
                title={allChecked ? "Deselect all" : "Select all untracked"}
              >
                {allChecked && <div className="h-2 w-2 bg-[#2c7a7b] rounded-sm" />}
              </button>
            </div>
            <div className="col-span-4">Keyword</div>
            <div className="col-span-1 flex justify-end">
              <SortHeader label="Pos" sortKey="competitor_rank" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
            </div>
            <div className="col-span-2 flex justify-end">
              <SortHeader label="Volume" sortKey="search_volume" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
            </div>
            <div className="col-span-1 flex justify-end">
              <SortHeader label={`CPC (${currencySymbolForLocation(client.location_name).trim() || "$"})`} sortKey="cpc" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
            </div>
            <div className="col-span-1 flex justify-end">
              <SortHeader label="KD" sortKey="keyword_difficulty" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
            </div>
            <div className="col-span-2 text-right">URL</div>
          </div>
          <div className="divide-y divide-[#edf2f7] max-h-[560px] overflow-y-auto">
            {sorted.map((r, i) => {
              const tracked = r.already_tracked;
              const checked = selected.has(r.keyword);
              return (
                <div
                  key={`${r.keyword}-${i}`}
                  data-testid={`gap-row-${i}`}
                  className="grid grid-cols-12 gap-x-3 px-4 py-2.5 hover:bg-[#f7fafc] text-sm items-center"
                >
                  <div className="col-span-1">
                    {tracked ? (
                      <span
                        title="Already in tracked keywords"
                        className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#4fd1c5]/15 text-[#2c7a7b]"
                      >
                        <BookmarkCheck className="h-3 w-3" />
                      </span>
                    ) : (
                      <button
                        onClick={() => toggleOne(r.keyword)}
                        data-testid={`gap-check-${i}`}
                        className="h-4 w-4 rounded border border-[#cbd5e0] bg-white inline-flex items-center justify-center hover:border-[#4fd1c5] transition-colors"
                      >
                        {checked && <div className="h-2 w-2 bg-[#2c7a7b] rounded-sm" />}
                      </button>
                    )}
                  </div>
                  <div className="col-span-4 truncate text-[#1a202c]" title={r.keyword}>{r.keyword}</div>
                  <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">
                    {r.competitor_rank ?? "—"}
                  </div>
                  <div className="col-span-2 text-right font-mono-tight text-[#4a5568]">
                    {fmt(r.search_volume)}
                  </div>
                  <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">
                    {formatCpc(r.cpc, client.location_name)}
                  </div>
                  <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">
                    {r.keyword_difficulty ?? "—"}
                  </div>
                  <div className="col-span-2 text-right truncate">
                    {r.competitor_url ? (
                      <a
                        href={r.competitor_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-[11px] text-[#2c7a7b] hover:text-[#1a202c] inline-flex items-center gap-1 font-mono-tight"
                        title={r.competitor_url}
                      >
                        <span className="truncate max-w-[180px]">{r.competitor_url.replace(/^https?:\/\//, "").replace(/^www\./, "")}</span>
                        <ArrowUpRight className="h-3 w-3 flex-none" />
                      </a>
                    ) : (
                      <span className="text-[#a0aec0]">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
